import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, ExternalLink, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../../lib/api";
import { AnimatedProgress, EASE } from "../../components/motion";
import { ErrorState, MotionButton, EmptyState } from "../../components/common";
import { LessonRow } from "./CourseDetail";

function toEmbed(url) {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

function LessonContent({ l }) {
  const box = "overflow-hidden rounded-2xl bg-slate-900 ring-1 ring-slate-200";
  return (
    <div className="space-y-6" data-testid="lesson-content">
      {l.type === "video" && l.url && (
        /\.(mp4|webm|ogg)(\?|$)/i.test(l.url) ? (
          <video src={l.url} controls className={`${box} aspect-video w-full`} />
        ) : (
          <div className={`${box} aspect-video`}><iframe src={toEmbed(l.url)} title={l.title} className="h-full w-full" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen /></div>
        )
      )}
      {l.type === "image" && l.url && <img src={l.url} alt={l.title} className="w-full rounded-2xl object-cover ring-1 ring-slate-200" />}
      {l.type === "pdf" && l.url && (
        <div className="space-y-3">
          <iframe src={l.url} title={l.title} className="h-[70vh] w-full rounded-2xl ring-1 ring-slate-200 bg-white" />
          <a href={l.url} target="_blank" rel="noopener noreferrer" className="btn-ghost inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-semibold" data-testid="lesson-pdf-open-link"><ExternalLink className="h-4 w-4" /> PDF'i yeni sekmede aç</a>
        </div>
      )}
      {l.content && <div className="whitespace-pre-line text-[15px] leading-relaxed text-slate-600">{l.content}</div>}
    </div>
  );
}

export default function Lesson() {
  const { courseId, lessonId } = useParams();
  const nav = useNavigate();
  const { data: c, loading, error, reload } = useApi(`/me/courses/${courseId}`);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.post(`/me/courses/${courseId}/lessons/${lessonId}/visit`).catch(() => {});
  }, [courseId, lessonId]);

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (loading) return <div className="grid gap-6 lg:grid-cols-[1fr_340px]"><div className="skeleton h-[480px]" /><div className="skeleton h-[480px]" /></div>;
  const all = c.modules.flatMap((m) => m.lessons.map((l) => ({ ...l, module: m.title })));
  const idx = all.findIndex((l) => l.id === lessonId);
  const l = all[idx];
  if (!l || l.status === "locked") return <EmptyState icon={Lock} title="Bu ders henüz kilitli." desc="Önceki dersleri tamamladıktan sonra erişebilirsiniz." testid="lesson-locked" />;
  const prev = all[idx - 1];
  const next = all[idx + 1];

  const complete = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/me/courses/${courseId}/lessons/${lessonId}/complete`);
      toast.success(`Ders tamamlandı · İlerleme %${r.data.progress}`);
      await reload();
      if (next) nav(`/panel/egitimlerim/${courseId}/ders/${next.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Link to={`/panel/egitimlerim/${courseId}`} className="group mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[var(--ink)]" data-testid="back-to-course-link">
        <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" /> {c.title}
      </Link>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <motion.div key={lessonId} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35, ease: EASE }} className="surface p-5 sm:p-7">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[var(--brand)]">{l.module}</p>
          <h1 className="mt-2 mb-6 text-xl sm:text-2xl font-extrabold tracking-tight text-[var(--ink)]" data-testid="lesson-title">{l.title}</h1>
          <LessonContent l={l} />
          <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-2">
              {prev && <Link to={`/panel/egitimlerim/${courseId}/ders/${prev.id}`} className="btn-ghost inline-flex h-11 items-center gap-2 rounded-xl px-4 text-sm font-semibold" data-testid="lesson-prev-btn"><ArrowLeft className="h-4 w-4" /> Önceki</Link>}
              {next && next.status !== "locked" && <Link to={`/panel/egitimlerim/${courseId}/ders/${next.id}`} className="btn-ghost inline-flex h-11 items-center gap-2 rounded-xl px-4 text-sm font-semibold" data-testid="lesson-next-btn">Sonraki <ArrowRight className="h-4 w-4" /></Link>}
            </div>
            {l.status === "completed" ? (
              <span className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-emerald-50 px-5 text-sm font-semibold text-emerald-700 ring-1 ring-emerald-100" data-testid="lesson-completed-badge"><CheckCircle2 className="h-4 w-4" /> Bu ders tamamlandı</span>
            ) : (
              <MotionButton onClick={complete} loading={busy} loadingText="Kaydediliyor..." className="h-12 w-full sm:w-auto" data-testid="lesson-complete-btn"><CheckCircle2 className="h-4 w-4" /> Dersi Tamamla</MotionButton>
            )}
          </div>
        </motion.div>
        <aside className="surface h-fit p-3 lg:sticky lg:top-8">
          <div className="px-3.5 pb-3 pt-2">
            <div className="mb-2 flex justify-between text-xs font-semibold"><span className="text-slate-500">Eğitim İlerlemesi</span><span data-testid="lesson-course-progress">%{c.progress}</span></div>
            <AnimatedProgress value={c.progress} />
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {c.modules.map((m) => (
              <div key={m.id} className="mb-2">
                <p className="px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">{m.title}</p>
                {m.lessons.map((x) => <LessonRow key={x.id} l={x} cid={courseId} current={x.id === lessonId} />)}
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
